/**
 * Data layer for `link-tester`. Thin typed fetch wrappers over /api/v1/link-tester.
 * Raw `fetch()` is confined to this file per the dash contract (rule D6).
 */

export type UserAgentPreset = "chrome" | "safari" | "firefox" | "mobile" | "bot";

export interface HeaderPair {
	key: string;
	value: string;
}

export interface TestLinkBody {
	url: string;
	userAgent: UserAgentPreset;
	headers?: HeaderPair[];
	cookies?: string;
}

export interface TestLinkHop {
	step: number;
	method: string;
	url: string;
	status: number;
	statusText: string;
	latency_ms: number;
	set_cookies: string[];
	response_headers: Record<string, string>;
	location?: string;
	error?: string;
}

export interface TestLinkResponse {
	hops: TestLinkHop[];
	total_latency_ms: number;
	final_url: string;
	final_status: number;
	hop_count: number;
	ok: boolean;
}

const BASE = "/api/v1/link-tester";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function testLink(body: TestLinkBody): Promise<TestLinkResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<TestLinkResponse>(res);
}

export const linkTesterQueryKeys = {
	all: ["link-tester"] as const,
	test: (url: string) => [...linkTesterQueryKeys.all, "test", url] as const,
};
